import { ListKeyManagerOption } from '@angular/cdk/a11y';
import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import {distinctUntilChanged, map, publishReplay, refCount, switchMap, take} from 'rxjs/operators';

import { Location, SetDefaultLocationRequest } from '../../../../atlas_sdk/api';
import { AtlasApiService } from '../../../../atlas_sdk/atlas.api.service';
import { BusinessNavConfigService, Config } from '../../../../config.service';
import { SnackbarService } from '../../../../core-components/snackbar/snackbar.service';
import { replaceUrlTokens } from '../../../../core-components/util';
import { BusinessNavDataService } from '../../../../data.service';
import { ProductLogoBankService } from '../../../../product-logo-bank.service';
import { AtlasTranslateService } from '../../../../translate/translate.service';
import { BusinessNavLocationService } from '../../location.service';

@Component({
  selector: 'atlas-location-list-item',
  templateUrl: './location-list-item.component.html',
  styleUrls: ['./location-list-item.component.scss']
})
export class LocationListItemComponent implements OnInit, ListKeyManagerOption {
  @Input() location: Location;
  @Input() isActive: boolean = false;
  /** Never disabled for now, this is here for the interface implementation. */
  disabled: boolean = false;
  isCurrentLocation: boolean = false;

  targetLink$: Observable<string>;
  targetPath$: Observable<string>;
  target$: Observable<string>;
  logoUrl$: Observable<string>;
  hasProduct$: Observable<boolean>;
  serviceProviderId: string;

  constructor(
    public dataService: BusinessNavDataService,
    public config: BusinessNavConfigService,
    private apiService: AtlasApiService,
    private snackbarService: SnackbarService,
    private t: AtlasTranslateService,
    private router: Router,
    public locationService: BusinessNavLocationService,
    public productLogoBankService: ProductLogoBankService,
    private elRef: ElementRef,
  ) {
    this.serviceProviderId = this.config.getCurrentConfig().serviceProviderId;
    this.hasProduct$ = this.locationService.activePath$
      .pipe(
        map(() => {
          const currentConfig = this.config.getCurrentConfig();
          if (!this.location.accountGroup) {
            return false;
          }
          if (!this.location.accountGroup.activatedProductIds) {
            return false;
          }
          return this.location.accountGroup.activatedProductIds.filter((activatedProductId: string) => activatedProductId === currentConfig.serviceProviderId).length > 0;
        }),
        publishReplay(1),
        refCount()
      );

    this.logoUrl$ = this.config.config$.pipe(
      map((config: Config) => {
        return this.productLogoBankService.getLogo(config.serviceProviderId);
      })
    );

    this.targetLink$ = this.locationService.activePath$
      .pipe(
        map((link: string) => {
          const currentConfig = this.config.getCurrentConfig();
          if (!!this.location.accountGroup && !!this.location.accountGroup.activatedProductIds) {
            const matchingProduct = this.location.accountGroup.activatedProductIds.filter(
              (activatedProductId: string) =>
                activatedProductId === currentConfig.serviceProviderId
            );
            if (matchingProduct.length !== 0) {
              if (currentConfig.defaultURL) {
                  link = currentConfig.defaultURL;
              }
              // Get the current nav item link, replace with target agid
              return replaceUrlTokens(link, this.location.accountGroup.accountGroupId, '');
            }
          }
          if (!!this.location.accountGroup) {
              return this.location.accountGroup.url + '/dashboard';
          } else if (!!this.location.brand) {
              return this.location.brand.url + '/dashboard';
          } else {
              return '#';
          }
        })
      );
    this.targetPath$ = this.locationService.activePath$.pipe(
      distinctUntilChanged(),
      map((url: string) => {
        const link = url.split('/').slice(url.startsWith('http') ? 3 : 1).join('/');
        if (!!this.location.accountGroup) {
          if (link && link.indexOf('<accountGroupId>') !== -1) {
            return replaceUrlTokens(link, this.location.accountGroup.accountGroupId, '');
          } else {
            return `/account/location/${this.location.accountGroup.accountGroupId}/dashboard`;
          }
        } else {
          const groupPath = this.location.brand.pathNodes.join('|');
          if (link && link.indexOf('<brandPath>') !== -1) {
            return replaceUrlTokens(link, '', groupPath);
          } else {
            return `/account/brands/${groupPath}/dashboard`;
          }
        }
      })
    );
    this.target$ = this.config.config$.pipe(
      switchMap(conf => {
        if (conf.serviceProviderId.toUpperCase() === 'VBC') {
          return this.targetPath$;
        } else {
          return this.targetLink$;
        }
      })
    );
  }

  ngOnInit(): void {
    const curConfig = this.config.getCurrentConfig();
    this.isCurrentLocation = (!!this.location.accountGroup && curConfig.accountId === this.location.accountGroup.accountGroupId) ||
      (!!this.location.brand && curConfig.groupPath === this.location.brand.pathNodes.join('|'));
  }

  setDefaultLocation(accountGroupId: string): void {
    const setDefaultSub = combineLatest([this.config.partnerId$, of(accountGroupId)])
      .pipe(
        switchMap(([partnerId, accountGroupId]: [string, string]) => {
          return this.apiService.setDefaultLocation(new SetDefaultLocationRequest({
            accountGroupId: accountGroupId,
            partnerId: partnerId
          }));
        })
      ).subscribe(
        () => {
          this.dataService.setOverridenDefaultLocation(accountGroupId);
          this.snackbarService.show(this.t.instant('COMMON.RESULT.SUCCESS', 'Success'), 'OK');
          setTimeout(() => {
            setDefaultSub.unsubscribe();
          }, 0);
        },
        () => {
          this.snackbarService.show(this.t.instant('COMMON.RESULT.FAILURE', 'Could not set default location'), 'OK', { duration: 4000 });
          setTimeout(() => {
            setDefaultSub.unsubscribe();
          }, 0);
        });
  }

  /** Gets the label for this option. */
  getLabel(): string {
    if (!!this.location.accountGroup) {
      return this.location.accountGroup.name;
    } else if (!!this.location.brand) {
      return this.location.brand.name;
    }
    // Location not provided yet, should probably not get here
    return '';
  }

  goToLocation(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isCurrentLocation) {
      return;
    }
    if (this.serviceProviderId.toUpperCase() === 'VBC') {
      this.targetPath$.pipe(take(1)).subscribe((path: string) => this.router.navigateByUrl(path));
    } else {
      this.targetLink$.pipe(take(1)).subscribe((link: string) => window.location.href = link);
    }
  }

  onScroll(): void {
    this.elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
